import { Phase, Emitter } from "./GameState.js";
import { mapLine } from "./LineMapper.js";
import { GRID } from "../config.js";

export class GameEngine {
    constructor(state) {
        this.state = state;
        this.changes = new Emitter();
        this._lines = []; // [{ text, chars: string[], startTime, endTime }]
    }

    subscribe(fn) {
        return this.changes.subscribe(fn);
    }

    _commit() {
        this.changes.emit(this.state);
    }

    // --- Music-driven events (called by TextAliveController) -----------------

    loadLines(lines) {
        this._lines = lines;
        this.state.phase = Phase.READY;
        this._commit();
    }

    // Active line changed: regenerate bars along a fresh path.
    enterLine(index) {
        const line = this._lines[index];
        if (!line) return;

        const charTexts = line.chars.map((c) => c.text);
        const { items, stepBudget, start } = mapLine(charTexts, GRID);

        this.state.phase = Phase.PLAYING;
        this.state.lineIndex = index;
        this.state.lineText = line.text;
        this.state.lineChars = charTexts;
        this.state.activeCharIndex = -1;
        this.state.items = items;
        this.state.moves = directionsAlong(items);
        this.state.collectedCount = 0;
        this.state.pathIndex = 0;
        this.state.combo = 0;
        this.state.feedbackMessage = "";
        this.state.feedbackType = null;
        this.state.stepsLeft = stepBudget;
        this.state.miku = { ...start };

        // Miku spawns on the first bar and starts the path from there.
        this._collectItem(this.state.items[0]);
        this._finishLineIfDone();
        this._commit();
    }

    // Single time source: maps a (smoothly interpolated) song position onto the
    // active line and the character being sung. Called every animation frame by
    // TextAliveController, but only commits when something actually changes.
    syncTime(position) {
        if (!this._lines.length) return;

        const idx = this._lines.findIndex(
            (l) => l.startTime <= position && position < l.endTime
        );
        if (idx !== -1 && idx !== this.state.lineIndex) {
            this.enterLine(idx);
        }

        const line = this._lines[this.state.lineIndex];
        if (!line) return;

        let charIdx = -1;
        for (let i = 0; i < line.chars.length; i++) {
            if (line.chars[i].startTime <= position) charIdx = i;
            else break;
        }

        if (charIdx !== this.state.activeCharIndex) {
            this.state.activeCharIndex = charIdx;
            this._commit();
        }
    }

    songEnded() {
        this.state.phase = Phase.SONG_END;
        this._commit();
    }

    // --- Input-driven events (called by InputController) ---------------------

    move(dir) {
        if (this.state.phase !== Phase.PLAYING) return;
        if (this.state.stepsLeft <= 0) return;

        const expectedDir = this.state.moves[this.state.pathIndex];
        if (!expectedDir) {
            this._finishLineIfDone();
            this._commit();
            return;
        }

        this.state.stepsLeft -= 1;

        if (dir === expectedDir) {
            this._advancePath({ collect: true });
        } else {
            this._advancePath({ collect: false });
        }

        this._commit();
    }

    _advancePath({ collect }) {
        const nextIndex = Math.min(this.state.pathIndex + 1, this.state.items.length - 1);
        const nextItem = this.state.items[nextIndex];
        if (!nextItem) return;

        this.state.pathIndex = nextIndex;
        this.state.miku = { col: nextItem.col, row: nextItem.row };

        if (collect) {
            this._collectItem(nextItem);
            this.state.combo += 1;
            this.state.feedbackMessage = "";
            this.state.feedbackType = null;
        } else {
            nextItem.missed = true;
            this.state.score = Math.max(0, this.state.score - 1);
            this.state.combo = 0;
            this.state.feedbackMessage = "Combo break! -1";
            this.state.feedbackType = "miss";
        }

        this._finishLineIfDone();
    }

    _collectItem(item) {
        if (!item || item.collected) return;

        item.collected = true;
        item.missed = false;
        this.state.collectedCount += 1;
        this.state.score += 1;
    }

    _finishLineIfDone() {
        if (this.state.pathIndex >= this.state.items.length - 1) {
            this.state.phase = Phase.LINE_DONE;
        }
    }
}

// The orthogonal direction taken between each consecutive pair of bars along
// the path: the sequence of arrow presses needed to walk the whole line.
function directionsAlong(items) {
    const moves = [];
    for (let i = 1; i < items.length; i++) {
        const dCol = items[i].col - items[i - 1].col;
        const dRow = items[i].row - items[i - 1].row;
        if (dCol === 1) moves.push("right");
        else if (dCol === -1) moves.push("left");
        else if (dRow === 1) moves.push("down");
        else if (dRow === -1) moves.push("up");
    }
    return moves;
}
